import { useCallback, useState, type FC } from "react";
import type { ButtonProps, ModalContentProps } from "@chakra-ui/react";
import {
	Button,
	Link,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	Text,
	useMultiStyleConfig,
	useTheme,
	type LinkProps,
	type SystemStyleObject
} from "@chakra-ui/react";
import { FaEllipsisV } from "react-icons/fa";
import type { NavMenuItemData } from "@/components/NavMenu/NavMenuItem";
import { htmlify } from "@/services/utils";
import { AccessibleModal } from "../AccessibleModal/AccessibleModal";
import { FlexColumn } from "../FlexColumn/FlexColumn";

// Displays on mobile
interface NavModalProps {
	buttonLabel?: string;
	items: NavMenuItemData[];
	onClick: (item: NavMenuItemData) => void;
	style: SystemStyleObject;
}

export const NavModal: FC<NavModalProps> = ({ buttonLabel, items, onClick, style }) => {
	const [isOpen, setOpen] = useState(false);
	const theme = useTheme();
	const styleParts = useMultiStyleConfig("NavModal");

	const handleClick = useCallback(
		(item: NavMenuItemData) => {
			onClick(item);
			setOpen(false);
		},
		[onClick]
	);

	return (
		<>
			<Button
				onClick={() => {
					return setOpen(true);
				}}
				variant="link"
				{...(styleParts.buttonLink as ButtonProps)}
			>
				<Text as="span">{buttonLabel ? buttonLabel : "Menu"}</Text>
				<FaEllipsisV />
			</Button>
			<AccessibleModal
				id="navigation-links-modal"
				isOpen={isOpen}
				onClose={() => {
					return setOpen(false);
				}}
				size="full"
			>
				<ModalContent {...(styleParts.modalContent as ModalContentProps)}>
					<ModalHeader border="none">
						<ModalCloseButton />
					</ModalHeader>
					<ModalBody>
						<FlexColumn gap={8}>
							{items.map((navLink) => {
								return (
									<Link
										sx={styleParts.modalContent}
										width="fit-content"
										key={navLink.value}
										onClick={() => {
											handleClick(navLink);
										}}
										{...(style as LinkProps)}
									>
										{htmlify(navLink.label)}
									</Link>
								);
							})}
						</FlexColumn>
					</ModalBody>
				</ModalContent>
			</AccessibleModal>
		</>
	);
};
